// Helpers de roles del frontend.
// Mantenemos compatibilidad con roles legacy.
export const ROLES = {
  admin: 'Admin',
  direccion: 'Dirección',
  administrativo: 'Administrativo',
  doctor: 'Doctor',
  enfermeria: 'Enfermería',
  anestesista: 'Anestesista',
  tablet: 'Tablet LOPD (kiosco)',
  tablet_consent: 'Tablet Consentimientos (kiosco)',
};

const LEGACY_MAP = {
  admin: 'admin',
  direccion: 'direccion',
  administrativo: 'administrativo',
  doctor: 'doctor',  // Phase 27 — sistema de citas (agenda propia)
  enfermeria: 'enfermeria',  // Phase 27.5 — ve todas las agendas, sin notas internas
  anestesista: 'anestesista',  // 2026-07-24 — solo quirófano y anestesia
  tablet: 'tablet',  // Phase 24 — kiosk iPad LOPD
  tablet_consent: 'tablet_consent',  // Phase 23.11 — kiosk iPad firmas
  medico: 'direccion',
  secretaria: 'direccion',
  recepcion: 'administrativo',
};

export function normalizeRole(role) {
  return LEGACY_MAP[(role || '').toLowerCase()] || 'administrativo';
}

export function isManagement(user) {
  const r = normalizeRole(user?.role);
  return r === 'admin' || r === 'direccion';
}

export function isAdmin(user) {
  return normalizeRole(user?.role) === 'admin';
}

/**
 * Roles que pueden AJUSTAR el hueco (forzar solapes o fuera de horario), poner
 * una hora manual y modificar los tiempos de la cita. Incluye a todo el
 * personal clínico: admin, dirección, administrativo, enfermería y también los
 * doctores (2026-07-16: todos deben poder poner/editar la hora manualmente).
 */
export function canForceSlot(user) {
  const r = normalizeRole(user?.role);
  return r === 'admin' || r === 'direccion' || r === 'administrativo' || r === 'enfermeria' || r === 'doctor';
}

/**
 * Phase 27 — Sistema de citas.
 * Si el usuario es `doctor`, solo ve/gestiona su propia agenda
 * (user.agenda_id, que llega enriquecido desde /api/auth/me).
 */
/** Perfiles CLÍNICOS: doctor y anestesista. Es la misma raya que traza el
 *  servidor con los datos económicos (`_staff_cobros`, 2026-08-26): no crean,
 *  no editan y no borran cobros. Tenerla con nombre evita que la pantalla y el
 *  servidor midan distinto — que es justo lo que pasaba: la pestaña de Cobros
 *  se le escondía al doctor y no al anestesista, y a este le salía un botón
 *  que el servidor le iba a rechazar (2026-09-17). */
export function isClinico(user) {
  const r = normalizeRole(user?.role);
  return r === 'doctor' || r === 'anestesista';
}

export function isDoctor(user) {
  return normalizeRole(user?.role) === 'doctor';
}

/**
 * Notas de administración del paciente (pagos, gestiones, recordatorios…).
 * SOLO administración y dirección: admin, dirección, administrativo. NI
 * enfermería (enfermería) NI médicos. Dirección las ve por ser admin (2026-07-13).
 * El backend lo vuelve a comprobar en /api/patients/{id}/notas-internas.
 */
/**
 * Administración: admin, dirección y administrativas.
 * Es quien puede ver PRESUPUESTOS — el importe es confidencial y no lo ven ni
 * enfermería, ni anestesistas, ni doctores (dirección, 2026-08-18). El servidor
 * lo comprueba igual con `require_gestion()`: esto es solo para no enseñar una
 * puerta que va a dar 403.
 */
export function isGestion(user) {
  const r = normalizeRole(user?.role);
  return r === 'admin' || r === 'direccion' || r === 'administrativo';
}

/**
 * Quién habla con el Agente IA (D7, 22/9/2026, dirección: «los doctores no;
 * solo el escribir con IA / ajustar la historia; pero no pregunta libre al
 * agente: eso solo administrativos, administradores y enfermería»).
 *
 * Es la MISMA lista que `auth.AGENTE_ROLES` del servidor, que es quien pone
 * la regla (doctor y anestesista reciben 403). Aquí solo sirve para no
 * ofrecerles la campana, el buscador del Agente ni el cuadro de la agenda.
 * Vive en lib/roles para que todas las pantallas midan igual: antes estaba
 * copiada en AppShell y la agenda (CitasPage) no la miraba.
 *
 * Se compara el rol CRUDO con los viejos escritos a mano, como hace el
 * servidor: con `normalizeRole`, un rol vacío o desconocido caería en
 * «administrativo» (su valor por defecto) y tendría Agente.
 */
const ROLES_CON_AGENTE = [
  'admin', 'direccion', 'administrativo', 'enfermeria',
  'medico', 'secretaria', 'recepcion',  // viejos: dirección / administrativo
];

export function tieneAgente(user) {
  return ROLES_CON_AGENTE.includes((user?.role || '').toLowerCase());
}

/**
 * Quién entra al chat interno: los de la sala «global» del servidor
 * (`chat_router._ROLES_GLOBAL`), que es quien pone la regla. Gestión con sus
 * roles viejos y, desde el 28/9/2026, enfermería (dirección: «en el chat
 * global incorpora también a [la enfermera]»). Enfermería ve solo la sala
 * global, como una administrativa: las salas las manda el servidor.
 *
 * Aquí solo sirve para el menú, el ⌘K, la ruta y el globo de lo sin leer.
 * Rol CRUDO, como el servidor: con `normalizeRole`, un rol vacío o
 * desconocido caería en «administrativo» y vería una puerta que da 403. Las
 * tablets no tienen este rol (son `tablet` y `tablet_consent`).
 */
const ROLES_CON_CHAT = [
  'admin', 'direccion', 'administrativo', 'enfermeria',
  'medico', 'secretaria', 'recepcion',  // viejos: dirección / administrativo
];

export function tieneChat(user) {
  return ROLES_CON_CHAT.includes((user?.role || '').toLowerCase());
}

/**
 * El equipo NO médico (administración, 14/10/2026): a quien se le apuntan
 * vacaciones, teletrabajo y días libres en Registro de jornada. Gestión —con
 * sus roles viejos, menos «medico», que es un médico— y enfermería. Los
 * doctores y la anestesia van por Agendas disponibles.
 *
 * Es la MISMA lista que `auth.EQUIPO_NO_MEDICO_ROLES` del servidor, que es
 * quien pone la regla. Aquí solo sirve para no enseñar la pestaña a quien el
 * servidor le va a contestar 403. Rol CRUDO, como el servidor.
 */
const ROLES_EQUIPO_NO_MEDICO = [
  'admin', 'direccion', 'administrativo', 'enfermeria',
  'secretaria', 'recepcion',  // viejos: dirección / administrativo
];

export function esEquipoNoMedico(user) {
  return ROLES_EQUIPO_NO_MEDICO.includes((user?.role || '').toLowerCase());
}

/**
 * «Mejoras software» (20/10/2026): quien tiene marcada esa casilla en
 * Usuarios, y NADIE MÁS. El rol no cuenta, tampoco el de admin: un admin sin
 * la casilla no ve el menú, y el servidor (`services/mejoras_software.
 * tiene_acceso`) le contesta 403. Tiene que ser `true` de verdad, del
 * personal (la lista de `auth.STAFF_ROLES`: la tablet nunca).
 */
const ROLES_DEL_PERSONAL = [
  'admin', 'direccion', 'administrativo', 'doctor', 'enfermeria', 'anestesista',
  'medico', 'secretaria', 'recepcion',
];

export function tieneMejorasSoftware(user) {
  return user?.mejoras_software === true
    && ROLES_DEL_PERSONAL.includes((user?.role || '').toLowerCase());
}

/**
 * Los roles que entran en Leads por sí mismos: gestión y sus roles viejos.
 * La misma lista que `auth.GESTION_ROLES` del servidor; rol CRUDO, como él.
 */
const ROLES_GESTION = [
  'admin', 'direccion', 'administrativo',
  'medico', 'secretaria', 'recepcion',  // viejos: dirección / administrativo
];

export function rolEntraEnLeads(role) {
  return ROLES_GESTION.includes((role || '').toLowerCase());
}

/**
 * «Leads: solo de estas campañas» (administración, 22/10/2026): quien su rol
 * NO entra en Leads (una doctora) y tiene campañas elegidas en Usuarios
 * (`leads_campanas`, ids de Campañas). Entra en Leads SOLO PARA VER, solo los
 * leads de esas campañas y sin selector para cambiar a otra. A un rol de
 * gestión no se le pone, y si lo tuviera guardado no cuenta. Es la regla de
 * `auth.entra_en_leads_por_el_ajuste` del servidor, que es quien la cumple;
 * aquí solo sirve para enseñar Leads y no ofrecer lo que el servidor niega.
 */
export function leadsAcotados(user) {
  const role = (user?.role || '').toLowerCase();
  return Array.isArray(user?.leads_campanas)
    && user.leads_campanas.some((c) => String(c ?? '').trim() !== '')
    && ROLES_DEL_PERSONAL.includes(role) && !rolEntraEnLeads(role);
}

/**
 * Quién ve «Leads / Contactos»: gestión, como siempre, y quien entra por el
 * ajuste de campañas (`leadsAcotados`). La misma regla que
 * `auth.require_leads` del servidor.
 */
export function veLeads(user) {
  return isGestion(user) || leadsAcotados(user);
}

/**
 * PACIENTE PROHIBIDO (administración, 14/10/2026). Quién MARCA y DESMARCA:
 * gestión (con sus roles viejos) y los doctores. Es la misma lista que
 * `services/paciente_prohibido._ROLES_QUE_MARCAN` del servidor, que es quien
 * pone la regla; aquí solo sirve para no enseñar el botón a quien le va a
 * contestar 403. Rol CRUDO, como el servidor. El resto ve el indicador.
 */
const ROLES_QUE_MARCAN_PROHIBIDO = [
  'admin', 'direccion', 'administrativo',
  'medico', 'secretaria', 'recepcion',  // viejos: dirección / administrativo
  'doctor',
];

export function puedeMarcarProhibido(user) {
  return ROLES_QUE_MARCAN_PROHIBIDO.includes((user?.role || '').toLowerCase());
}

/** La excepción para dar cita a un paciente prohibido: solo admin y
 *  dirección (`auth.is_management` del servidor). */
export function puedeExcepcionProhibido(user) {
  return isManagement(user);
}

/**
 * Notas de administración del paciente (pagos, gestiones, recordatorios…).
 * Por su rol, administración y dirección: admin, dirección, administrativo
 * (dirección las ve por ser admin, 2026-07-13). Y desde el 27/10/2026, quien
 * tenga en Usuarios la casilla «Notas de administración: también esta
 * persona» (`notas_administracion`, a quien su rol no las tiene: doctor,
 * enfermería, anestesista), para leer y escribir. Es la regla de
 * `auth.puede_notas_administracion` del servidor, que es quien la cumple en
 * /api/patients/{id}/notas-internas; aquí solo sirve para enseñar la pestaña
 * y los resúmenes a quien no le va a dar 403.
 */
function rolTieneNotasAdministracion(role) {
  const r = normalizeRole(role);
  return r === 'admin' || r === 'direccion' || r === 'administrativo';
}

export function notasAdministracionPorAjuste(user) {
  const role = (user?.role || '').toLowerCase();
  return user?.notas_administracion === true
    && ROLES_DEL_PERSONAL.includes(role) && !rolTieneNotasAdministracion(role);
}

export function canSeeInternalNotes(user) {
  return rolTieneNotasAdministracion(user?.role) || notasAdministracionPorAjuste(user);
}

/**
 * «Drive: entra en estas carpetas» (administración, 27/10/2026): quien su
 * rol NO tiene ClinicPilot Drive (hoy solo el admin lo tiene) y tiene
 * carpetas elegidas en Usuarios (`drive_carpetas`, ids de carpetas del
 * Drive). Entra en el Drive SOLO con esas carpetas y lo que cuelga de ellas,
 * para leer y escribir documentos, sin organizar carpetas. Es la regla de
 * `auth.entra_en_drive_por_el_ajuste` del servidor, que es quien la cumple
 * en cada ruta; aquí solo sirve para enseñar el menú, dejar pasar la ruta y
 * no ofrecer en la pantalla lo que el servidor niega.
 */
export function driveAcotado(user) {
  const role = (user?.role || '').toLowerCase();
  return Array.isArray(user?.drive_carpetas)
    && user.drive_carpetas.some((c) => String(c ?? '').trim() !== '')
    && ROLES_DEL_PERSONAL.includes(role) && !isAdmin(user) && !driveTodo(user);
}

/**
 * «Drive: todo (como el administrador)» (administración, 3/11/2026): quien
 * su rol NO tiene ClinicPilot Drive y tiene la casilla en Usuarios
 * (`drive_todo`). Entra en el Drive ENTERO, como el admin: todas las
 * carpetas, la bandeja general, organizar carpetas, el contador y quién entra
 * en cada carpeta. Con la casilla, sus carpetas elegidas no cuentan
 * (`driveAcotado` da false). Es la regla de
 * `auth.entra_en_drive_completo_por_el_ajuste` del servidor. Al admin no le
 * hace falta (lo tiene por su rol): `driveCompleto` junta los dos.
 */
export function driveTodo(user) {
  const role = (user?.role || '').toLowerCase();
  return user?.drive_todo === true && ROLES_DEL_PERSONAL.includes(role) && !isAdmin(user);
}

/** Quién tiene el Drive COMPLETO: el admin por su rol o quien tiene la casilla
 *  «Drive: todo». La misma regla que `auth.drive_completo` del servidor
 *  (`require_drive_completo` en las rutas de organizar, `/uso` y accesos). */
export function driveCompleto(user) {
  return isAdmin(user) || driveTodo(user);
}

/** Quién ve «ClinicPilot Drive»: quien lo tiene completo (`driveCompleto`) y
 *  quien entra por el ajuste de carpetas (`driveAcotado`). La misma regla que
 *  `auth.require_drive` del servidor. */
export function veDrive(user) {
  return driveCompleto(user) || driveAcotado(user);
}

/**
 * Phase 24 — Modo kiosko (iPad mostrador LOPD).
 * Si el usuario es `tablet`, la app oculta sidebar y topbar y no le deja
 * entrar en el programa del equipo.
 *
 * Desde el 29/9/2026 la tablet es UNA cuenta con dos modos: las dos cuentas
 * de tablet van al selector «Firmar consentimientos / Rellenar LOPD» o al
 * modo recordado en ese iPad (`lib/modoTablet`, `esCuentaDeTablet`). El
 * quiosco ya no lo decide el rol.
 */
export function isTablet(user) {
  return normalizeRole(user?.role) === 'tablet';
}

/**
 * Phase 23.11 — Modo kiosko (iPad consentimientos).
 * Si el usuario es `tablet_consent`, lo mismo que `tablet` (arriba): desde el
 * 29/9/2026 también elige su modo en el iPad.
 */
export function isTabletConsent(user) {
  return normalizeRole(user?.role) === 'tablet_consent';
}


export function roleLabel(role) {
  return ROLES[normalizeRole(role)] || role || '—';
}

/** 2026-07-24 — Anestesistas: solo ven Quirófano y Anestesia. */
export function isAnestesista(user) {
  return normalizeRole(user?.role) === 'anestesista';
}

/**
 * Quién lleva el catálogo de medicamentos favoritos: añadir, cambiar y
 * borrar plantillas (M34, informe del 22/9/2026). Los sanitarios y
 * dirección; recepción las lee para hacer una receta, pero no las toca.
 *
 * Es la MISMA lista que `recipes_router._CATALOGO_MEDICAMENTOS`, que es quien
 * pone la regla. Aquí solo sirve para no enseñar «Añadir favorito», «Editar»
 * y la papelera a quien el servidor le va a contestar 403 (segunda pasada,
 * 23/9/2026: el servidor se cerró y la pantalla seguía ofreciéndolo).
 *
 * Rol CRUDO, sin `normalizeRole`, porque así compara `require_role` en el
 * servidor: las dos listas son literalmente la misma, roles viejos incluidos.
 */
const ROLES_CATALOGO_MEDICAMENTOS = [
  'admin', 'direccion', 'medico', 'secretaria',  // dirección y sus roles viejos
  'doctor', 'anestesista', 'enfermeria',          // sanitarios
];

export function llevaCatalogoMedicamentos(user) {
  return ROLES_CATALOGO_MEDICAMENTOS.includes(user?.role || '');
}
